<template>
  <section>
    <ul v-if="type === 'customerNews'" class="flex flex-wrap sm:-mx-3 mt-7">
      <slot />
      <ellevio-web-card-image-item
        v-for="(item, index) in fetchedData"
        :key="item.id"
        class="breadcrumbColumnInitialAnimation"
        :style="customAnimation(index)"
        :two-every-row="true"
        :use-white-bg="true"
        :no-padding-x="true"
        :large-heading="true"
        :use-headings="true"
        :heading="item.heading"
        :text="item.text"
        :image-url="item.imageUrl"
        :link-url="item.linkUrl"
        :date="item.date"
        :date-text="item.dateText"
        :tag="item.tag"
        :company="item.identitySender"
        :lineClamp="lineClamp || item.lineClamp" />
    </ul>
    <template v-if="type === 'searchResult'">
      <slot />
        <ellevio-web-search-result-item
          v-for="searchHit in searchHits"
          :key="searchHit.url"
          :newHeaderLayout="true"
          :heading="searchHit.title"
          :excerpt="searchHit.excerpt"
          :url="searchHit.url"
          :breadcrumbs="searchHit.breadCrumb"
          :date="searchHit.publishDate"
          :date-text="getDateText(searchHit.publishDate)" />
    </template>
    <template v-if="type === 'faqSearchResult'">
      <slot />
      <ellevio-web-search-result-item v-for="(item, index) in fetchedData" :key="item.id" class="breadcrumbColumnInitialAnimation" :style="customAnimation(index)" :heading="item.heading" :excerpt="item.text" :url="item.linkUrl" />
    </template>
    <ul v-if="type === 'pressItems'" class="flex flex-wrap -mb-8">
      <slot />
      <ellevio-web-card-image-item
        v-for="(item, index) in fetchedData"
        :key="item.id"
        class="breadcrumbColumnInitialAnimation"
        :style="customAnimation(index)"
        :one-every-row="true"
        :use-white-bg="true"
        :no-padding-x="true"
        :large-heading="true"
        :use-headings="true"
        :heading="item.heading"
        :text="item.text"
        :image-url="item.imageUrl"
        :link-url="item.linkUrl"
        :date="item.date"
        :date-text="item.dateText"
        :tag="item.tag"
        :company="item.identitySender"
        :lineClamp="lineClamp || item.lineClamp" />
    </ul>
    <ul v-if="type === 'guideCollectionItems'" class="flex flex-wrap -mb-8">
      <slot />
      <ellevio-web-card-image-item
        v-for="(item, index) in fetchedData"
        :key="item.id"
        class="breadcrumbColumnInitialAnimation"
        :style="customAnimation(index)"
        :one-every-row="true"
        :use-white-bg="true"
        :no-padding-x="true"
        :large-heading="true"
        :use-headings="true"
        :heading="item.heading"
        :text="item.text"
        :image-url="item.imageUrl"
        :link-url="item.linkUrl"
        :date="item.date"
        :date-text="item.dateText"
        :tag="item.tag"
        :company="item.identitySender"
        :lineClamp="lineClamp || item.lineClamp" />
    </ul>
    <div class="mt-10 text-center">
      <p v-if="!inEnglish && showItemsCount" class="mb-6 font-bold">
        {{ `${existingItemsCount} av ${totalItemsCount} ${typeDescription} visas` }}
      </p>
      <button v-if="!inEnglish && showMoreButton" class="w-full md:w-auto btn-secondary btn-icon-plus" @click.prevent="fetchItems(existingItemsCount + 1)" data-testid="loadMoreButton">
        {{ `Ladda ${additionalItemsCount} ${typeDescription} till` }}
      </button>

      <!--in english typeDescription should be 'news items'-->
      <p v-if="inEnglish && showItemsCount" class="mb-6 font-bold">
        {{ `${existingItemsCount} out of ${totalItemsCount} ${typeDescription} shown` }}
      </p>
      <button v-if="inEnglish && showMoreButton" class="w-full md:w-auto btn-secondary btn-icon-plus" @click.prevent="fetchItems(existingItemsCount + 1)" data-testid="loadMoreButton">
        {{ `Show another ${additionalItemsCount} ${typeDescription}` }}
      </button>
    </div>
  </section>
</template>

<script>
import EllevioWebCardImageItem from '../structure/CardImageItem.vue'
import EllevioWebSearchResultItem from '../structure/SearchResultItem.vue'

import axios from 'axios'

export default {
  name: 'EllevioWebShowMoreItems',
  components: {
    EllevioWebCardImageItem,
    EllevioWebSearchResultItem
  },
  props: {
    additionalItems: {
      type: Number
    },
    totalItems: {
      type: Number
    },
    typeDescription: {
      type: String
    },
    type: {
      type: String,
      required: true
    },
    apiUrl: {
      type: String,
      required: true
    },
    apiParams: {
      type: String,
      required: false
    },
    inEnglish: {
      type: Boolean,
      default: false
    },
    showItemsCount: {
      type: Boolean,
      default: true
    },
    story: {
      type: Boolean,
      default: false
    },
    lineClamp: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fetchedData: [],
      additionalItemsCount: 0,
      existingItemsCount: 0,
      totalItemsCount: 0,
      animationCounter: 0,
      searchHits: [],
    }
  },
  computed: {
    showMoreButton() {
      return this.existingItemsCount < this.totalItems
    }
  },
  mounted() {
    this.additionalItemsCount = this.additionalItems
    this.existingItemsCount = this.additionalItems
    this.totalItemsCount = this.totalItems
  },
  methods: {
    refreshIndex: function (newIndex) {
      const params = new URLSearchParams(window.location.search)
      params.set('index', newIndex)

      const newUrl = window.location.origin + window.location.pathname + '?' + params.toString()

      history.replaceState(null, document.title, newUrl)
    },
    getDateText(date) {
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    results: async function (fromId) {
      const extraParams = new URLSearchParams(this.apiParams ?? '')
      extraParams.set('index', fromId)
      return await axios.get(`${this.apiUrl}?${extraParams}`)
    },
    fetchItems: async function (fromId) {
      this.animationCounter = 0
      let responseData = null

      const response = await this.results(fromId)
      responseData = response.data.data

      this.fetchedData.push(...(responseData?.items ?? []))

      // necessary for the type "searchResult" since the response has a different structure
      if (responseData?.result?.hits.length > 0) {
        const {    
          result: { hits }
        } = response.data.data
        
        this.searchHits.push(...(hits ?? []))
      }
      
      if (this.existingItemsCount < this.totalItemsCount) {
        if (this.existingItemsCount + this.additionalItemsCount > this.totalItemsCount) {
          this.existingItemsCount = this.totalItemsCount
        } else {
          this.existingItemsCount += this.additionalItemsCount
        }
      }

      this.refreshIndex(this.existingItemsCount)
    },
    customAnimation(index) {
      let initialItems = this.existingItemsCount - 2 * this.additionalItemsCount
      if (index < initialItems) {
        return `opacity: 1;`
      } else {
        return `animation: breadcrumbColumnAnimation 300ms ease-out ${(index - initialItems) * 100}ms; animation-fill-mode: forwards;`
      }
    }
  }
}
</script>
